<template>
  <AuthLayout :mode="mode">
    <Section
      :heading="
        type && type === 'powercast'
          ? 'Selective Powercast Email'
          : 'Selective Broadcast Email'
      "
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <template v-if="contacts && contacts.length > 0">
          <AppTable :mode="mode" :columns="columns">
            <template #checkbox>
              <AppCheckbox
                :mode="mode"
                id="select-all"
                v-model="isAllSelected"
                @change="toggleSelectAll"
              />
            </template>
            <template #rows>
              <tr v-for="(contact, key) in contacts" :key="key">
                <td>
                  <AppCheckbox
                    :mode="mode"
                    :id="key"
                    :value="contact._source.contact.contact_id"
                    v-model="selectedContactIds"
                  />
                </td>
                <td>
                  <RouterLink
                    :to="{
                      name: 'member-details',
                      params: { id: contact._source.member.member_id },
                    }"
                    v-if="contact._source.member.company_name"
                    v-tooltip="`${contact._source.member.company_name}`"
                  >
                    <span
                      class="block max-w-[250px] truncate sm:max-w-[120px]"
                      >{{ contact._source.member.company_name }}</span
                    >
                  </RouterLink>
                  <span v-else>-</span>
                </td>
                <td>
                  <FallbackName
                    :to="{
                      name: 'contact-details',
                      params: {
                        id: contact._source.contact.contact_id,
                      },
                    }"
                    :first="contact._source.contact.first_name"
                    :last="contact._source.contact.last_name"
                  />
                </td>
              </tr>
            </template>
          </AppTable>
          <div class="mt-6 sm:mt-4">
            <AppButton
              :title="type === 'powercast' ? 'Powercast' : 'Broadcast'"
              @action="addListing"
              :isDisabled="selectedContactIds.length === 0"
            />
          </div>
        </template>
        <EmptyData v-else :notFound="'No data represented!'" :mode="mode" />
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import EmptyData from "@C/Common/EmptyData.vue";
import AuthLayout from "@A/AuthLayout.vue";
import FallbackName from "@C/Tables/FallbackName.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "SelectiveBroadcastEmail",
  components: {
    AuthLayout,
    Section,
    EmptyData,
    FallbackName,
    AppCard,
    AppCheckbox,
    AppTable,
    AppButton,
  },
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  data() {
    return {
      contacts: [],
      listing: {},
      selectedContactIds: [],
      type: null,
      columns: [
        {
          name: "",
          classes: "w-5",
        },
        {
          name: "Company",
        },
        {
          name: "Contact",
        },
      ],
    };
  },
  created() {
    this.type = this.$route?.query?.type;
  },
  mounted() {
    if (!this.type) {
      return;
    }
    if (this.type === "powercast") {
      this.listing = localStorage.getItem("selective-send-powercast")
        ? JSON.parse(localStorage.getItem("selective-send-powercast"))
        : null;
    } else if (this.type === "broadcast") {
      this.listing = localStorage.getItem("selective-send-broadcast")
        ? JSON.parse(localStorage.getItem("selective-send-broadcast"))
        : null;
    } else {
      this.listing = null;
    }
    if (!this.listing) {
      return;
    }
    this.getContacts();
  },
  methods: {
    async getContacts() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        instant_buy: true,
        network_subscriber: true,
        mfr_company: this.listing.mfr,
      };
      try {
        const { data } = await ApiRequest().post(`/contact_search`, payload);
        if (data) {
          this.contacts = data;
          this.selectAll();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async addListing() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        mailqueue: {
          recipient_list: this.selectedContactIds.join(","),
        },
      };
      if (this.type === "powercast") {
        payload.powersearch = this.listing;
      } else {
        payload.listing = this.listing;
      }
      try {
        const { data } = await ApiRequest().post(
          `/${this.type === "powercast" ? "powercast" : "listing"}`,
          payload,
        );
        if (data) {
          this.toastStore.show(
            "Thank you, your message has been sent",
            "success",
          );
          if (this.type === "powercast") {
            localStorage.removeItem("selective-send-powercast");
            this.$router.push({ name: "broadcast-email" });
          } else {
            localStorage.removeItem("selective-send-broadcast");
            this.$router.push({ name: "add-listing" });
          }
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    selectAll() {
      this.selectedContactIds = this.contacts.map(
        (contact) => contact._source.contact.contact_id,
      );
    },
    unselectAll() {
      this.selectedContactIds = [];
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.unselectAll();
      } else {
        this.selectAll();
      }
    },
  },
  computed: {
    isAllSelected() {
      return this.selectedContactIds.length === this.contacts.length;
    },
  },
};
</script>
